// @docs:
//   - docs/wip/CREW_PAPER_REQUESTS.md  § API contract (CrewRequestView), § Providing and sharing
// ==============================================
// How one requested paper reads on the crew checklist (Crew-3). An item is
// "ready" once it is answered: a paper attached (shared or not yet), or "not
// needed". The skipper sees nothing until the crew member shares.
// ==============================================

import type { CrewItemView, CrewRequest } from './types';
import { accessEndDate, fmtDay, firstName } from './dates';

export type ItemTone = 'todo' | 'ready' | 'shared' | 'skipped' | 'attention' | 'ended';

/** What to photograph or attach, in the design's words. */
const HINTS: Record<string, string> = {
  passport: 'Photo of the page with your picture',
  nzeta: 'Screenshot or PDF of the approval',
  visa: 'Screenshot or PDF of the visa',
  travel_insurance: 'Certificate or policy summary',
  emergency_contact: 'Name, relationship, phone',
  sea_survival: 'Photo or PDF of the certificate',
  medical: 'Photo or PDF of the certificate',
  competency: 'Photo or PDF of the certificate',
  radio_operator: 'Photo or PDF of the certificate',
};
export const itemHint = (type: string) => HINTS[type] ?? 'Photo or PDF';

/** Crew & People type labels (vortex-api catalog.ts TYPES.crew) — for lists outside a request. */
const CREW_LABELS: Record<string, string> = {
  passport: 'Passport', competency: 'Certificate of competency / STCW', radio_operator: 'Radio operator certificate', medical: 'Medical certificate',
  visa: 'Visa', nzeta: 'NZeTA or NZ visa', travel_insurance: 'Travel & medical insurance', emergency_contact: 'Emergency contact',
  sea_survival: 'Sea survival certificate', other: 'Other personal document',
};
export const crewTypeLabel = (type: string) => CREW_LABELS[type] ?? type;

/** Papers that usually carry an expiry date (vortex-api catalog `expires`). */
const EXPIRES = new Set(['passport', 'competency', 'radio_operator', 'medical', 'visa', 'nzeta', 'travel_insurance', 'sea_survival', 'other']);
export const itemExpires = (type: string) => EXPIRES.has(type);

export const isAnswered = (i: CrewItemView) => i.state !== 'requested' || !!i.paper;
export const isSharedNow = (i: CrewItemView) => !!i.grant?.active;

export function itemStatus(i: CrewItemView, req: Pick<CrewRequest, 'requesterName' | 'timezone'>): { tone: ItemTone; text: string } {
  const who = firstName(req.requesterName);
  if (i.state === 'not_needed') return { tone: 'skipped', text: i.reason ? `Not needed · ${i.reason}` : 'Not needed' };
  if (i.state === 'needs_attention') return { tone: 'attention', text: i.question ? `${who} has a question about this` : `Shared · ${who} will take a look` };
  if (i.grant?.active) return { tone: 'shared', text: `Shared until ${fmtDay(accessEndDate(i.grant.expiresAt, req.timezone))}` };
  if (i.state === 'accepted') return { tone: 'shared', text: 'Accepted' };
  if (i.grant && !i.grant.active && i.state === 'provided') return { tone: 'ended', text: `${who}’s access has ended` };
  if (i.paper) return { tone: 'ready', text: 'Ready to share' };
  return { tone: 'todo', text: itemHint(i.type) };
}

export function progress(items: CrewItemView[]): { ready: number; total: number } {
  return { ready: items.filter(isAnswered).length, total: items.length };
}

export const isWritable = (req: Pick<CrewRequest, 'status'>) => req.status === 'open' || req.status === 'complete';

export function closedMessage(req: Pick<CrewRequest, 'status' | 'requesterName'>): string | null {
  const who = firstName(req.requesterName);
  if (req.status === 'cancelled') return `${who} cancelled this request. Nothing you added is shared with ${who} any more, and your papers stay in your own papers.`;
  if (req.status === 'closed') return `This request has closed. Your papers stay in your own papers.`;
  return null;
}
